import Box from "@mui/material/Box";
import { SxProps, Theme, alpha } from "@mui/material/styles";
import { KeyboardEvent, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";

import { colors } from "@/theme";

interface PillSwitchProps {
  tabs: { key: string; label: ReactNode; icon?: ReactNode }[];
  /** Index of the side that is on. */
  value: number;
  onChange: (index: number) => void;
  ariaLabel: string;
  /** `sm` for a second switch under a first: the lesser of two choices. */
  size?: "md" | "sm";
  /** Fills the width it is given at every screen size, each side an equal share. */
  fullWidth?: boolean;
  /**
   * Scrolls sideways when the tabs do not fit, instead of squeezing them. `sx`
   * then goes on the scroll area, not on the track.
   */
  scrollable?: boolean;
  sx?: SxProps<Theme>;
}

const FADE = 32;

const reducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * A switch between a few views of one thing: casino or sports history, deposit
 * or withdraw.
 *
 * One track, the side that is on filled in the brand blue with its icon, the
 * others waiting beside it - so the control that changes everything under it
 * reads as a switch and not as a row of buttons of which one happens to be
 * darker. A tab list to a screen reader and to the keyboard: the arrow keys
 * move between the sides.
 */
const PillSwitch = ({
  tabs,
  value,
  onChange,
  ariaLabel,
  size = "md",
  fullWidth = false,
  scrollable = false,
  sx,
}: PillSwitchProps) => {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const scroller = useRef<HTMLDivElement | null>(null);
  const firstScroll = useRef(true);
  const [edges, setEdges] = useState({ left: false, right: false });
  const small = size === "sm";

  const measure = useCallback(() => {
    const node = scroller.current;
    if (!node) return;
    const left = node.scrollLeft > 1;
    const right = node.scrollLeft + node.clientWidth < node.scrollWidth - 1;
    setEdges((prev) => (prev.left === left && prev.right === right ? prev : { left, right }));
  }, []);

  useEffect(() => {
    const node = scroller.current;
    if (!scrollable || !node) return;

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    if (node.firstElementChild) observer.observe(node.firstElementChild);
    node.addEventListener("scroll", measure, { passive: true });
    return () => {
      observer.disconnect();
      node.removeEventListener("scroll", measure);
    };
  }, [scrollable, measure, tabs.length]);

  // Keeps the tab that is on in view, so a deep link to the last tab shows it on a phone.
  useEffect(() => {
    const node = scroller.current;
    const tab = refs.current[value];
    if (!scrollable || !node || !tab) return;

    const box = node.getBoundingClientRect();
    const rect = tab.getBoundingClientRect();
    const left = rect.left - box.left + node.scrollLeft - (box.width - rect.width) / 2;
    node.scrollTo({
      left,
      behavior: firstScroll.current || reducedMotion() ? "auto" : "smooth",
    });
    firstScroll.current = false;
  }, [scrollable, value]);

  const page = (direction: 1 | -1) => {
    const node = scroller.current;
    if (!node) return;
    node.scrollBy({
      left: direction * node.clientWidth * 0.7,
      behavior: reducedMotion() ? "auto" : "smooth",
    });
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const last = tabs.length - 1;
    const next =
      event.key === "ArrowRight"
        ? (value + 1) % tabs.length
        : event.key === "ArrowLeft"
          ? (value - 1 + tabs.length) % tabs.length
          : event.key === "Home"
            ? 0
            : event.key === "End"
              ? last
              : -1;
    if (next < 0) return;

    event.preventDefault();
    onChange(next);
    refs.current[next]?.focus({ preventScroll: scrollable });
  };

  const track = (
    <Box
      role="tablist"
      aria-label={ariaLabel}
      onKeyDown={onKeyDown}
      sx={[
        (theme) => ({
          display: fullWidth ? "flex" : "inline-flex",
          alignSelf: fullWidth ? "stretch" : { xs: "stretch", sm: "flex-start" },
          gap: 0.5,
          p: 0.5,
          borderRadius: 999,
          border: "1px solid",
          borderColor: alpha(colors.prime, 0.2),
          backgroundColor: alpha(colors.prime, 0.06),
          ...theme.applyStyles("dark", { backgroundColor: alpha(colors.prime, 0.1) }),
        }),
        // Fills a phone when the tabs fit, grows past it (and scrolls) when they do not.
        scrollable && {
          display: "flex",
          width: fullWidth ? "100%" : { xs: "100%", sm: "fit-content" },
          minWidth: "max-content",
        },
        ...(scrollable ? [] : Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {tabs.map((tab, index) => {
        const selected = index === value;

        return (
          <Box
            key={tab.key}
            component="button"
            type="button"
            role="tab"
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            ref={(node: HTMLButtonElement | null) => {
              refs.current[index] = node;
            }}
            onClick={() => onChange(index)}
            sx={{
              flex: fullWidth ? 1 : { xs: 1, sm: "none" },
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: small ? 0.75 : 1,
              minHeight: small ? 32 : 40,
              px: small ? 2 : 2.5,
              border: 0,
              cursor: "pointer",
              borderRadius: 999,
              fontFamily: "inherit",
              fontSize: small ? 13 : 14,
              fontWeight: 700,
              whiteSpace: "nowrap",
              color: selected ? colors.white : "text.secondary",
              backgroundColor: selected ? colors.prime : "transparent",
              boxShadow: selected ? `0 4px 12px ${alpha(colors.prime, 0.35)}` : "none",
              transition: "background-color 180ms ease, color 180ms ease, box-shadow 180ms ease",
              "& svg": { fontSize: small ? 16 : 19, flexShrink: 0 },
              "@media (hover: hover)": {
                "&:hover": selected
                  ? {}
                  : { color: "text.primary", backgroundColor: alpha(colors.prime, 0.12) },
              },
              "&:focus-visible": { outline: "2px solid #5BC8FF", outlineOffset: 2 },
              "@media (prefers-reduced-motion: reduce)": { transition: "none" },
            }}
          >
            {tab.icon}
            {tab.label}
          </Box>
        );
      })}
    </Box>
  );

  if (!scrollable) return track;

  // Fades the side that has more tabs past it, so a cut-off pill reads as "scroll".
  const mask = `linear-gradient(to right, ${edges.left ? "transparent" : "#000"} 0, #000 ${
    edges.left ? FADE : 0
  }px, #000 calc(100% - ${edges.right ? FADE : 0}px), ${edges.right ? "transparent" : "#000"} 100%)`;

  return (
    <Box sx={[{ position: "relative", minWidth: 0 }, ...(Array.isArray(sx) ? sx : [sx])]}>
      <Box
        ref={scroller}
        sx={{
          overflowX: "auto",
          overscrollBehaviorX: "contain",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
          // Room for the selected pill's shadow and focus ring, which the scroll box would clip.
          pt: 0.5,
          pb: 1.5,
          mt: -0.5,
          mb: -1.5,
          maskImage: mask,
          WebkitMaskImage: mask,
        }}
      >
        {track}
      </Box>

      {/* Touch screens swipe; a mouse has no way to find a hidden scrollbar. */}
      {(["left", "right"] as const).map((side) => (
        <Box
          key={side}
          component="button"
          type="button"
          tabIndex={-1}
          aria-hidden
          onClick={() => page(side === "left" ? -1 : 1)}
          sx={(theme) => ({
            display: "none",
            "@media (hover: hover) and (pointer: fine)": {
              display: edges[side] ? "inline-flex" : "none",
            },
            position: "absolute",
            top: "50%",
            [side]: 4,
            transform: "translateY(-50%)",
            alignItems: "center",
            justifyContent: "center",
            width: 28,
            height: 28,
            p: 0,
            borderRadius: "50%",
            border: "1px solid",
            borderColor: alpha(colors.prime, 0.25),
            backgroundColor: theme.palette.background.paper,
            color: "text.primary",
            cursor: "pointer",
            boxShadow: `0 2px 8px ${alpha("#000", 0.25)}`,
            "& svg": { fontSize: 18 },
            "&:hover": { borderColor: colors.prime, color: colors.prime },
          })}
        >
          {side === "left" ? <MdChevronLeft /> : <MdChevronRight />}
        </Box>
      ))}
    </Box>
  );
};

export default PillSwitch;
